import { useReportStore } from '../../../stores/useReportStore';
import ChannelBenchmarksTable from '../widgets/ChannelBenchmarksTable';
import RingGauge from '../widgets/RingGauge';
import Section from '../widgets/Section';
import FunnelBars, { type FunnelStage } from '../widgets/FunnelBars';
import SplitBar from '../widgets/SplitBar';
import BasisBadge from '../widgets/BasisBadge';
import { Card, CardContent } from '@/components/ui/card';
import { labelsFor } from '../../../data/reportLabels';
import type { AuditVertical, FunnelData, FunnelStageItem } from '../../../types/audit';

/** Overview stages with the same headline-consistency rule the old trapezoid
 * had: when the dual-funnel breakdown exists, the last stage equals the SUM
 * of the breakdown categories so this number can never disagree with the
 * "where paid subscribers come from" section below. */
function overviewStages(funnel: FunnelData, vertical?: AuditVertical): FunnelStage[] {
  let paid = funnel.paid;
  if (funnel.combined) {
    const cb = funnel.combined;
    const breakdownSum =
      (cb.trialConverts || 0)
      + (cb.directPurchases || 0)
      + (cb.resubscriptions ?? 0)
      + (cb.productChanges ?? 0);
    if (breakdownSum > 0) {
      paid = { ...paid, value: breakdownSum, label: 'Paid conversions' };
    }
  }
  const labels = labelsFor(vertical);
  return [
    { data: funnel.visitors, fallback: labels.funnelTop },
    { data: funnel.signups, fallback: labels.funnelMid },
    { data: paid, fallback: labels.funnelBottom },
  ]
    .filter(
      (s): s is { data: NonNullable<typeof s.data>; fallback: string } =>
        s.data != null && s.data.value != null,
    )
    .map((s) => ({ label: s.data.label || s.fallback, value: s.data.value }));
}

function pathStages(stages: FunnelStageItem[]): FunnelStage[] {
  return stages
    .filter((s) => s.value != null)
    .map((s) => ({ label: s.stage, value: s.value }));
}

const SPLIT_COLORS = [
  'var(--report-blue)',
  'var(--report-green)',
  'var(--report-amber)',
  'var(--report-pink)',
];

export default function Performance() {
  const data = useReportStore((s) => s.reportData);
  if (!data) return null;

  const raw = data as unknown as Record<string, unknown>;
  const funnel = raw.funnel as FunnelData | undefined;
  const channelBenchmarks = raw.channelBenchmarks;
  const scorecard = Array.isArray(raw.scorecard) ? raw.scorecard : [];
  const vertical = raw._vertical as AuditVertical | undefined;
  const isEcommerce = vertical === 'ecommerce' || vertical === 'generic';
  const labels = labelsFor(vertical);

  const hasFunnel = funnel && Object.keys(funnel).length > 0;
  const hasChannels = channelBenchmarks !== null && channelBenchmarks !== undefined;
  const hasScorecard = scorecard.length > 0;

  if (!hasFunnel && !hasChannels && !hasScorecard) {
    return <p className="text-muted-foreground p-8 text-center">No performance data available.</p>;
  }

  const overview = hasFunnel ? overviewStages(funnel!, vertical) : [];
  const isDual =
    hasFunnel &&
    funnel!.funnelType === 'dual' &&
    Array.isArray(funnel!.trialFunnel) && funnel!.trialFunnel.length > 0 &&
    Array.isArray(funnel!.directFunnel) && funnel!.directFunnel.length > 0 &&
    funnel!.combined != null;

  const combined = isDual ? funnel!.combined! : null;
  const breakdown = combined
    ? [
        { value: combined.trialConverts ?? 0, label: 'Trial conversions', pct: combined.trialSharePct },
        { value: combined.directPurchases ?? 0, label: 'Direct subscriptions', pct: combined.directSharePct },
        { value: combined.resubscriptions ?? 0, label: 'Resubscriptions', pct: combined.resubscriptionsPct },
        { value: combined.productChanges ?? 0, label: 'Product changes', pct: combined.productChangesPct },
      ].filter((c) => c.value > 0)
    : [];
  const displayTotal = breakdown.reduce((sum, c) => sum + c.value, 0) || (combined?.totalPaid ?? 0);
  // Top-to-bottom conversion, computed from the stages the bars already show —
  // the funnel's one-number summary belongs in its title, not in the reader's
  // head. Deterministic here, so the compiler cannot disagree with the bars.
  const overallPct =
    overview.length >= 2 && (overview[0]?.value ?? 0) > 0
      ? ((overview[overview.length - 1]?.value ?? 0) / overview[0]!.value) * 100
      : null;

  return (
    <div>
      {overview.length > 0 && (
        <div className={isDual ? 'grid grid-cols-1 gap-x-5 xl:grid-cols-2' : undefined}>
          <Section
            title="Conversion funnel"
            hint={`${
              vertical === 'b2b'
                ? 'Full-funnel performance from website visitors through to a closed-won deal'
                : isEcommerce
                  ? 'Full-funnel performance from visitors through to completed purchase'
                  : 'Full-funnel performance from installs through to a paid subscription'
            }${overallPct != null ? ` · ${overallPct.toFixed(1)}% overall` : ''}.`}
          >
            <Card>
              <CardContent className="px-5.5 py-1">
                <FunnelBars stages={overview} color="var(--report-blue)" />
              </CardContent>
            </Card>
          </Section>

          {isDual && (
            <Section
              title="Trial funnel"
              hint="Bar width is proportional to real volume — the drop tells the story."
            >
              <Card>
                <CardContent className="px-5.5 py-1">
                  <FunnelBars stages={pathStages(funnel!.trialFunnel!)} color="var(--report-amber)" />
                </CardContent>
              </Card>
            </Section>
          )}
        </div>
      )}

      {isDual && (
        <Section title="Direct purchase path" hint="Subscribers who skip the trial entirely.">
          <Card>
            <CardContent className="px-5.5 py-1">
              <FunnelBars stages={pathStages(funnel!.directFunnel!)} color="var(--report-green)" />
            </CardContent>
          </Card>
        </Section>
      )}

      {breakdown.length > 0 && (
        <Section
          title="Where paid conversions come from"
          hint={
            displayTotal > 0
              ? `${displayTotal.toLocaleString()} paid conversions${combined?.totalPaidPct != null ? ` · ${combined.totalPaidPct.toFixed(1)}% of ${labels.funnelTop.toLowerCase()}` : ''}`
              : undefined
          }
        >
          <Card>
            <CardContent className="px-5.5 py-1">
              <SplitBar
                segments={breakdown.map((c, i) => ({
                  value: c.value,
                  label: c.label,
                  color: SPLIT_COLORS[i % SPLIT_COLORS.length],
                }))}
              />
              <div className="grid grid-cols-[repeat(auto-fit,minmax(150px,1fr))] gap-2.5">
                {breakdown.map((c, i) => (
                  <div key={c.label} className="rounded-[10px] border border-border px-4 py-3.5">
                    <div className="text-xl font-bold tabular-nums text-foreground">
                      {c.value.toLocaleString()}
                      <span
                        className="ml-1.5 inline-block size-2 rounded-full align-middle"
                        style={{ background: SPLIT_COLORS[i % SPLIT_COLORS.length] }}
                      />
                    </div>
                    <div className="mt-0.5 text-xs text-muted-foreground">
                      {c.label}
                      {c.pct != null ? ` · ${c.pct}%` : ''}
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </Section>
      )}

      {hasChannels && (
        <Section
          title="Channel benchmarks"
          hint="Performance comparison across your active acquisition channels."
        >
          <Card>
            <CardContent className="p-0">
              <ChannelBenchmarksTable channels={channelBenchmarks as any} vertical={vertical} />
            </CardContent>
          </Card>
        </Section>
      )}

      {hasScorecard && (
        <Section
          title="Business maturity scorecard"
          hint="Multi-dimensional assessment of business health against peer benchmarks."
        >
          <div className="grid grid-cols-[repeat(auto-fit,minmax(210px,1fr))] gap-2.5">
            {scorecard.map((dim: any) => (
              <div key={dim.label} className="flex">
                <RingGauge
                  value={dim.score}
                  label={dim.label}
                  benchmark={dim.benchmark}
                  color={dim.color}
                  detail={dim.detail}
                  footer={<BasisBadge basis={dim.basis} className="mt-1.5" />}
                />
              </div>
            ))}
          </div>
        </Section>
      )}
    </div>
  );
}
